import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatDateRange, formatDate } from "@/lib/dateUtils";
import EmptyState from "@/components/EmptyState";
import { CuteLoader } from "@/components/CuteLoader";

export default function PayHistoryPage() {
  const navigate = useNavigate();
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadHistory = useCallback(async () => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    try {
      const res = await api.get("/employee/periods", { signal: controller.signal });
      const finalized = res.data.filter(p => p.status === 'finalized');
      setPeriods(finalized);
      setError(null);
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('Request timed out');
      } else {
        setError('Could not load pay history');
      }
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (loading) {
    return <CuteLoader />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAF7F1] flex items-center justify-center p-6">
        <div className="text-center">
          <div className="text-[#9C6B4A] text-lg mb-4 font-serif">{error}</div>
          <button
            onClick={() => { setLoading(true); setError(null); loadHistory(); }}
            className="px-6 py-3 bg-[#9C6B4A] text-white rounded-lg hover:bg-[#8B5A39] transition font-medium"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF7F1] p-6 pb-24">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-serif text-[#231E1A] mb-2">Pay History</h1>
        <p className="text-sm text-[#9C6B4A] mb-6">View your finalized pay periods</p>
        
        {periods.length === 0 && (
          <EmptyState
            testId="pay-history-empty"
            line="Your pay history is on its way"
            sub="Every finalized period lands here after payday, with its recap a tap away."
          />
        )}
        
        <div className="space-y-3">
          {periods.map((period) => (
            <div
              key={period.period_id}
              onClick={() => navigate(`/employee/payday-recap/${period.period_id}`)}
              className="bg-white rounded-lg p-5 cursor-pointer hover:shadow-md transition border border-[#EAE1D4]"
            >
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-[#231E1A] font-serif">
                    {formatDateRange(period.start_date, period.end_date)}
                  </div>
                  {period.check_date && (
                    <div className="text-sm text-[#9C6B4A] mt-1">
                      Check date: {formatDate(period.check_date, { month: "long", day: "numeric" })}
                    </div>
                  )}
                </div>
                <span className="text-[#B0905E] text-sm font-medium">View Recap →</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}